import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import GoToTop from '../components/GotToTop';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { useAdvertisementEventServices } from '../apis/advertisementEventService';
import { useSponsorServices } from '../apis/sponsorService';
import { useAdvertisementServices } from '../apis/advertisementService';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths, isSameMonth, isSameDay, isToday } from 'date-fns';
import { ChevronLeft, ChevronRight, Star, Calendar, Clock, MapPin, FileText, Image as ImageIcon, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageFlipViewer from '../components/PageFlipViewer';
import { FeaturedEventSkeleton, CalendarSkeleton, EventCardSkeleton, SponsorCardSkeleton, AdvertisementCardSkeleton } from '../components/SkeletonLoader';
import { PublicProtectedBlock } from '../components/PublicProtectedHtml';

const AdvertisementPage = () => {
  const [featuredEvent, setFeaturedEvent] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [advertisements, setAdvertisements] = useState([]);
  const [adPage, setAdPage] = useState(1);
  const [adTotalPages, setAdTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedPdf, setSelectedPdf] = useState({ url: null, title: null });
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDateEventsModal, setShowDateEventsModal] = useState(false);

  const { getFeaturedAdvertisementEvent, getPublicAdvertisementEvents, getCalendarEvents } = useAdvertisementEventServices();
  const { getPublicSponsors } = useSponsorServices();
  const { getPublicAdvertisements } = useAdvertisementServices();

  useEffect(() => {
    // Initialize AOS safely
    try {
      if (typeof window !== 'undefined' && typeof AOS !== 'undefined') {
        if (!AOS.initialized) {
          AOS.init({
            duration: 800,
            once: false,
            mirror: true,
          });
        } else {
          AOS.refresh();
        }
      }
    } catch (error) {
      console.warn('AOS initialization error:', error);
    }
    fetchData();
  }, [currentPage]);

  useEffect(() => {
    fetchCalendarEvents();
  }, [currentMonth]);

  useEffect(() => {
    fetchAdvertisements();
  }, [adPage]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch featured event
      const featuredResponse = await getFeaturedAdvertisementEvent();
      if (featuredResponse.success) {
        setFeaturedEvent(featuredResponse.data);
      }

      // Fetch upcoming events with pagination
      const eventsResponse = await getPublicAdvertisementEvents({
        page: currentPage,
        limit: 6
      });
      if (eventsResponse.success) {
        setUpcomingEvents(eventsResponse.data.events || []);
        setTotalPages(eventsResponse.data.pagination?.pages || 1);
      }

      // Fetch sponsors
      const sponsorsResponse = await getPublicSponsors();
      if (sponsorsResponse.success) {
        setSponsors(sponsorsResponse.data || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load advertisement data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdvertisements = async () => {
    try {
      const response = await getPublicAdvertisements({
        page: adPage,
        limit: 10
      });
      
      if (response && response.success) {
        const adsData = response.data?.advertisements || response.data || [];
        setAdvertisements(Array.isArray(adsData) ? adsData : []);
        setAdTotalPages(response.data?.pagination?.pages || 1);
      }
    } catch (err) {
      console.error('Error fetching advertisements:', err);
    }
  };

  const stripHtmlTags = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, ' ').replace(/\s{2,}/g, ' ').trim();
  };

  const fetchCalendarEvents = async () => {
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await getCalendarEvents(year, month);
      if (response.success) {
        setCalendarEvents(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching calendar events:', err);
    }
  };

  const getEventsForDate = (date) => {
    return calendarEvents.filter(event => {
      // Handle regular events
      if (event.startDate) {
        const eventDate = new Date(event.startDate);
        const sponsorDate = event.sponsorDayDate ? new Date(event.sponsorDayDate) : null;
        const deadlineDate = event.deadlineDate ? new Date(event.deadlineDate) : null;
        
        return isSameDay(eventDate, date) || 
               (sponsorDate && isSameDay(sponsorDate, date)) ||
               (deadlineDate && isSameDay(deadlineDate, date));
      }
      // Handle sponsor days (from Sponsor model)
      if (event.isSponsorDay && event.sponsorDayDate) {
        const sponsorDate = new Date(event.sponsorDayDate);
        return isSameDay(sponsorDate, date);
      }
      return false;
    });
  };

  const getDateDots = (date) => {
    const eventsForDate = getEventsForDate(date);
    const dots = [];
    
    eventsForDate.forEach(event => {
      // Handle regular events
      if (event.startDate) {
        const eventDate = new Date(event.startDate);
        if (isSameDay(eventDate, date)) {
          dots.push('blue');
        }
        if (event.sponsorDayDate && isSameDay(new Date(event.sponsorDayDate), date)) {
          dots.push('orange');
        }
        if (event.deadlineDate && isSameDay(new Date(event.deadlineDate), date)) {
          dots.push('red');
        }
      }
      // Handle sponsor days (from Sponsor model)
      if (event.isSponsorDay && event.sponsorDayDate) {
        const sponsorDate = new Date(event.sponsorDayDate);
        if (isSameDay(sponsorDate, date)) {
          dots.push('orange');
        }
      }
    });
    
    return [...new Set(dots)]; // Remove duplicates
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startDayOfWeek = getDay(monthStart);

  const formatEventDate = (startDate, endDate, startTime) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : null;
    
    if (end && !isSameDay(start, end)) {
      return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`;
    }
    return `${format(start, 'MMM d')}${startTime ? ` - ${startTime}` : ''}`;
  };

  const formatEventTime = (startDate, startTime) => {
    const date = new Date(startDate);
    const dayName = format(date, 'EEE');
    return `${dayName}. ${format(date, 'MMM d')}${startTime ? ` - ${startTime}` : ''}`;
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <Header />

      {/* Main Content */}
      <section className="container mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-blue-900" data-aos="fade-up">
            Alenalki Advertisement
          </h1>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Featured Event Skeleton */}
            <div className="lg:col-span-1">
              <FeaturedEventSkeleton />
            </div>
            {/* Calendar Skeleton */}
            <div className="lg:col-span-1">
              <CalendarSkeleton />
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500">{error}</p>
            <button
              onClick={fetchData}
              className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Featured Event Card - Top Left */}
            <div className="lg:col-span-1" data-aos="fade-right">
              {featuredEvent ? (
                <div 
                  className="rounded-lg overflow-hidden shadow-lg h-full relative"
                  style={{
                    backgroundImage: featuredEvent.imageUrl 
                      ? `url("${encodeURI(featuredEvent.imageUrl)}")` 
                      : featuredEvent.backgroundColor 
                        ? featuredEvent.backgroundColor 
                        : 'linear-gradient(to bottom right, #2563eb, #1e40af)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    width: '100%',
                    height: '100%',
                    boxSizing: 'border-box',
                    minHeight: '400px'
                  }}
                >
                  {/* Overlay for better text readability - lighter overlay to show image */}
                  {featuredEvent.imageUrl && (
                    <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-black/30 to-black/40"></div>
                  )}
                  {!featuredEvent.imageUrl && (
                    <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60"></div>
                  )}
                  
                  <PublicProtectedBlock className="p-6 text-white h-full flex flex-col relative z-10">
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="text-yellow-400 fill-yellow-400" size={20} />
                      <span className="text-sm font-semibold">Featured</span>
                    </div>
                    
                    <h2 className="text-3xl font-bold mb-3">{featuredEvent.title}</h2>
                    <p className="text-blue-100 mb-4 line-clamp-3">{featuredEvent.description}</p>
                    
                    <div className="flex flex-wrap gap-4 mb-4 text-sm">
                      {featuredEvent.location && (
                        <div className="flex items-center gap-2">
                          <MapPin size={16} />
                          <span>{featuredEvent.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Calendar size={16} />
                        <span>{formatEventDate(featuredEvent.startDate, featuredEvent.endDate, featuredEvent.startTime)}</span>
                      </div>
                    </div>
                    
                    <div className="mt-auto flex gap-3">
                      {featuredEvent.actionButtonText && featuredEvent.actionButtonUrl && (
                        <a
                          href={featuredEvent.actionButtonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-2 bg-white text-blue-600 rounded-md font-semibold hover:bg-blue-50 transition-colors"
                        >
                          {featuredEvent.actionButtonText}
                        </a>
                      )}
                      <Link
                        to="/sponsor-application"
                        className="px-6 py-2 bg-transparent border-2 border-white text-white rounded-md font-semibold hover:bg-white/10 transition-colors"
                      >
                        Become Sponsor
                      </Link>
                    </div>
                  </PublicProtectedBlock>
                </div>
              ) : (
                <div className="bg-gray-200 rounded-lg p-8 text-center text-gray-500 h-full flex items-center justify-center">
                  No featured event available
                </div>
              )}
            </div>

            {/* Calendar Widget - Top Right */}
            <div className="lg:col-span-1" data-aos="fade-left">
              <div className="bg-white rounded-lg shadow-lg p-6 h-full">
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <h3 className="text-xl font-bold text-gray-800">
                    {format(currentMonth, 'MMMM yyyy')}
                  </h3>
                  <button
                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                    className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="mb-4">
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                      <div key={idx} className="text-center text-sm font-semibold text-gray-600 py-2">
                        {day}
                      </div>
                    ))}
                  </div>
                  
                  <div className="grid grid-cols-7 gap-1">
                    {/* Empty cells for days before month starts */}
                    {Array.from({ length: startDayOfWeek }).map((_, idx) => (
                      <div key={`empty-${idx}`} className="aspect-square"></div>
                    ))}
                    
                    {/* Days of the month */}
                    {daysInMonth.map((day, idx) => {
                      const dots = getDateDots(day);
                      const isCurrentDay = isToday(day);
                      const eventsForDate = getEventsForDate(day);
                      const hasEvents = eventsForDate.length > 0;
                      
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            if (hasEvents) {
                              setSelectedDate(day);
                              setShowDateEventsModal(true);
                            }
                          }}
                          className={`aspect-square flex flex-col items-center justify-center text-sm rounded-md transition-colors ${
                            isCurrentDay 
                              ? 'bg-blue-600 text-white font-semibold' 
                              : hasEvents
                              ? 'hover:bg-blue-50 cursor-pointer'
                              : 'hover:bg-gray-100'
                          }`}
                        >
                          <span>{format(day, 'd')}</span>
                          {dots.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {dots.includes('blue') && <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>}
                              {dots.includes('orange') && <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>}
                              {dots.includes('red') && <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>}
                            </div>
                          )}
                          {hasEvents && (
                            <span className="text-[10px] mt-0.5 text-gray-600 font-medium">
                              {eventsForDate.length} {eventsForDate.length === 1 ? 'event' : 'events'}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Legend */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex flex-wrap gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Event</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                      <span>Sponsor day</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span>Deadline</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bottom Section: Upcoming Events and Sponsors */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Events - Bottom Left */}
          <div data-aos="fade-up">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Upcoming Events</h2>
            
            {loading ? (
              <div className="space-y-4">
                <EventCardSkeleton count={3} />
              </div>
            ) : upcomingEvents.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No upcoming events
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {upcomingEvents.map((event, index) => (
                    <div
                      key={event.id}
                      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow"
                      data-aos="fade-up"
                      data-aos-delay={index * 100}
                      style={event.backgroundColor ? { backgroundColor: event.backgroundColor } : {}}
                    >
                      <PublicProtectedBlock className="p-6">
                        <div className="flex items-start gap-4">
                          {event.imageUrl && (
                            <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                              <img 
                                src={event.imageUrl} 
                                alt={event.title}
                            className="w-full h-full object-contain"
                              />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-800 mb-2">{event.title}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                              <div className="flex items-center gap-1">
                                <Clock size={14} />
                                <span>{formatEventTime(event.startDate, event.startTime)}</span>
                              </div>
                              {event.location && (
                                <div className="flex items-center gap-1">
                                  <MapPin size={14} />
                                  <span>{event.location}</span>
                                </div>
                              )}
                            </div>
                            {event.actionButtonText && event.actionButtonUrl && (
                              <a
                                href={event.actionButtonUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 transition-colors"
                              >
                                {event.actionButtonText}
                              </a>
                            )}
                          </div>
                        </div>
                      </PublicProtectedBlock>
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-gray-700">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Sponsors - Bottom Right */}
          <div data-aos="fade-up">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Sponsors</h2>
            
            {loading ? (
              <div className="grid grid-cols-2 gap-4">
                <SponsorCardSkeleton count={4} />
              </div>
            ) : sponsors.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                No sponsors available
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-6">
                  Thanks to our sponsorship inquiries. Thanks to our partners.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {sponsors.map((sponsor, index) => (
                    <div
                      key={sponsor.id}
                      className="bg-gray-800 rounded-lg p-6 flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer"
                      data-aos="fade-up"
                      data-aos-delay={index * 50}
                      onClick={() => sponsor.websiteUrl && window.open(sponsor.websiteUrl, '_blank')}
                    >
                      {sponsor.logoUrl ? (
                        <img 
                          src={sponsor.logoUrl} 
                          alt={sponsor.name}
                          className="max-w-full max-h-16 object-contain"
                        />
                      ) : (
                        <span className="text-white font-semibold">{sponsor.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Advertisements Section - Below Events and Sponsors */}
        <div className="mt-12" data-aos="fade-up">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Advertisements</h2>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <AdvertisementCardSkeleton count={6} />
            </div>
          ) : advertisements.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No advertisements available
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                {advertisements.map((ad, index) => {
                  const plainTextContent = stripHtmlTags(ad.content);
                  
                  return (
                    <div
                      key={ad.id}
                      className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:transform hover:scale-105"
                      data-aos="fade-up"
                      data-aos-delay={index * 100}
                    >
                      <PublicProtectedBlock className="flex flex-col h-full">
                        {/* Advertisement Card Header */}
                        <div className="h-48 relative bg-gray-200 flex items-center justify-center">
                          {ad.imageUrl ? (
                            <img 
                              src={ad.imageUrl} 
                              alt={ad.title}
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/20 to-blue-900/80 flex items-center justify-center">
                              <ImageIcon className="text-white text-5xl" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 flex items-end justify-start p-4">
                            <h3 className="text-xl font-bold text-white">
                              {ad.title}
                            </h3>
                          </div>
                        </div>

                        {/* Advertisement Card Content */}
                        <div className="p-6 flex flex-col flex-grow">
                          <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                            <span className="flex items-center">
                              <Calendar size={14} className="mr-1" />
                              {format(new Date(ad.createdAt), 'MMM dd, yyyy')}
                            </span>
                            <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-semibold">
                              {ad.category}
                            </span>
                          </div>

                          <div className="mb-4 h-24 overflow-hidden">
                            <p className="text-gray-600 text-sm line-clamp-4">
                              {plainTextContent.length > 200
                                ? `${plainTextContent.substring(0, 200)}...`
                                : plainTextContent}
                            </p>
                          </div>

                          <div className="flex items-center justify-between mt-auto">
                            <Link
                              to={`/advertisement/${ad.id}`}
                              className="text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center"
                            >
                              Read More
                              <ChevronRight size={16} className="ml-1" />
                            </Link>

                            {ad.pdfUrl && (
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedPdf({ url: ad.pdfUrl, title: ad.title });
                                  setShowPdfViewer(true);
                                }}
                                className="flex items-center text-red-600 hover:text-red-800 text-sm"
                              >
                                <FileText className="mr-1" size={14} />
                                PDF
                              </button>
                            )}
                          </div>
                        </div>
                      </PublicProtectedBlock>
                    </div>
                  );
                })}
              </div>

              {/* Advertisements Pagination */}
              {adTotalPages > 1 && (
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => setAdPage(prev => Math.max(1, prev - 1))}
                    disabled={adPage === 1}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2 text-gray-700">
                    Page {adPage} of {adTotalPages}
                  </span>
                  <button
                    onClick={() => setAdPage(prev => Math.min(adTotalPages, prev + 1))}
                    disabled={adPage === adTotalPages}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <GoToTop />
      <Footer />

      {/* PDF Flipbook Viewer Modal */}
      {showPdfViewer && selectedPdf.url && (
        <PageFlipViewer
          pdfUrl={selectedPdf.url}
          title={selectedPdf.title || "PDF Document"}
          onClose={() => {
            setShowPdfViewer(false);
            setSelectedPdf({ url: null, title: null });
          }}
        />
      )}

      {/* Date Events Modal */}
      {showDateEventsModal && selectedDate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-800">
                Events on {format(selectedDate, 'MMMM d, yyyy')}
              </h3>
              <button
                onClick={() => {
                  setShowDateEventsModal(false);
                  setSelectedDate(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {getEventsForDate(selectedDate).length === 0 ? (
                <p className="text-gray-500 text-center py-8">No events scheduled for this date.</p>
              ) : (
                <div className="space-y-4">
                  {getEventsForDate(selectedDate).map((event, index) => (
                    <PublicProtectedBlock
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <h4 className="font-semibold text-gray-800 mb-2">{event.title || event.name || 'Event'}</h4>
                      {event.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{event.description}</p>
                      )}
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        {event.startDate && (
                          <div className="flex items-center gap-1">
                            <Calendar size={14} />
                            <span>{formatEventDate(event.startDate, event.endDate, event.startTime)}</span>
                          </div>
                        )}
                        {event.location && (
                          <div className="flex items-center gap-1">
                            <MapPin size={14} />
                            <span>{event.location}</span>
                          </div>
                        )}
                      </div>
                      {event.actionButtonText && event.actionButtonUrl && (
                        <a
                          href={event.actionButtonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block mt-3 px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 transition-colors"
                        >
                          {event.actionButtonText}
                        </a>
                      )}
                    </PublicProtectedBlock>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvertisementPage;
